import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Loader2,
  FileText,
  FileSpreadsheet,
  Archive,
  Wallet,
  AlertCircle,
  DollarSign,
  Building2,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { money } from "@/lib/tax";
import { RevealEmail } from "@/components/RevealEmail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EXT = { csv: "csv", pdf: "pdf", zip: "zip" };

export default function AdminReports() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    payment_status: "all",
    category: "all",
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const payload = () => ({
    start_date: filters.start_date || null,
    end_date: filters.end_date || null,
    payment_status: filters.payment_status,
    category: filters.category,
  });

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/reports/summary", payload());
      setReport(data);
    } catch (e) {
      toast.error("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.post(`/admin/reports/export/${fmt}${fmt === "csv" ? "?reveal=true" : ""}`, payload(), {
        responseType: "blob",
      });
      const mime =
        fmt === "csv" ? "text/csv" : fmt === "pdf" ? "application/pdf" : "application/zip";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cheltenham-batch-report-${new Date().toISOString().slice(0, 10)}.${EXT[fmt]}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`Could not export ${fmt.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  const s = report?.summary;

  const Stat = ({ icon: Icon, label, value, tone, testId }) => {
    const tones = {
      blue: "bg-blue-50 text-[#1E3A8A]",
      green: "bg-emerald-50 text-emerald-700",
      amber: "bg-amber-50 text-amber-700",
    };
    return (
      <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={testId}>
        <div className="flex items-center gap-3">
          <span className={`w-10 h-10 rounded-md flex items-center justify-center ${tones[tone] || tones.blue}`}>
            <Icon className="w-5 h-5" />
          </span>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
            <p className="font-mono-data text-xl font-semibold text-slate-900">{value}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-[#1E3A8A]" /> Batch Reports
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Generate period summaries and export filings for the finance office.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label>From date</Label>
            <Input type="date" value={filters.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="report-start-date" />
          </div>
          <div className="space-y-2">
            <Label>To date</Label>
            <Input type="date" value={filters.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="report-end-date" />
          </div>
          <div className="space-y-2">
            <Label>Payment status</Label>
            <Select value={filters.payment_status} onValueChange={(v) => set("payment_status", v)}>
              <SelectTrigger data-testid="report-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="unpaid">Owes (unpaid)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={filters.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="report-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                <SelectItem value="wholesale">Wholesale</SelectItem>
                <SelectItem value="retail">Retail</SelectItem>
                <SelectItem value="biz">Business Privilege</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-5">
          <Button onClick={generate} disabled={loading} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="generate-report-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
            Generate Report
          </Button>
          <div className="flex items-center gap-2 sm:ml-auto">
            <Button variant="outline" className="border-slate-300" disabled={!report || exporting === "pdf"} onClick={() => doExport("pdf")} data-testid="export-report-pdf">
              {exporting === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />} PDF
            </Button>
            <Button variant="outline" className="border-slate-300" disabled={!report || exporting === "csv"} onClick={() => doExport("csv")} data-testid="export-report-csv">
              {exporting === "csv" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />} CSV
            </Button>
            <Button variant="outline" className="border-slate-300" disabled={!report || exporting === "zip"} onClick={() => doExport("zip")} data-testid="export-report-zip">
              {exporting === "zip" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />} ZIP of PDFs
            </Button>
          </div>
        </div>
      </div>

      {!report ? (
        <div className="border border-dashed border-slate-300 rounded-md p-16 text-center bg-white" data-testid="report-empty">
          <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Choose filters and generate a report to see the summary.</p>
        </div>
      ) : (
        <div data-testid="report-results">
          <p className="text-sm text-slate-500 mb-3">
            Period: <span className="font-medium text-slate-700">{report.period}</span> ·{" "}
            <span className="font-medium text-slate-700">{s.count}</span> filing(s)
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Stat icon={Wallet} label="Collected" value={money(s.total_collected)} tone="green" testId="report-collected" />
            <Stat icon={AlertCircle} label="Outstanding" value={money(s.total_outstanding)} tone="amber" testId="report-outstanding" />
            <Stat icon={DollarSign} label="Total Assessed" value={money(s.total_assessed)} tone="blue" testId="report-assessed" />
            <Stat icon={CheckCircle2} label="Paid / Unpaid" value={`${s.paid_count} / ${s.unpaid_count}`} tone="blue" testId="report-counts" />
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <Stat icon={Building2} label="Wholesale Tax" value={money(s.cat_wholesale)} tone="blue" testId="report-cat-wholesale" />
            <Stat icon={Building2} label="Retail Tax" value={money(s.cat_retail)} tone="blue" testId="report-cat-retail" />
            <Stat icon={Building2} label="Business Privilege Tax" value={money(s.cat_biz)} tone="blue" testId="report-cat-biz" />
          </div>

          {report.filings.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-md p-12 text-center bg-white">
              <p className="text-slate-500">No filings match these filters.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="report-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr className="text-left">
                    <th className="px-5 py-3 font-medium">Business</th>
                    <th className="px-5 py-3 font-medium hidden md:table-cell">Filer</th>
                    <th className="px-5 py-3 font-medium hidden sm:table-cell">Submitted</th>
                    <th className="px-5 py-3 font-medium text-center">Status</th>
                    <th className="px-5 py-3 font-medium text-right">Total Due</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.filings.map((f) => (
                    <tr key={f.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate(`/filing/${f.id}`)} data-testid={`report-row-${f.id}`}>
                      <td className="px-5 py-3">
                        <p className="font-medium text-slate-900">{f.business_name}</p>
                        <p className="text-xs text-slate-400 font-mono-data">{f.confirmation_number}</p>
                      </td>
                      <td className="px-5 py-3 text-slate-600 hidden md:table-cell">
                        <RevealEmail
                          masked={f.filer_email}
                          testId={`report-email-${f.id}`}
                          onReveal={async () => {
                            const { data } = await api.get(`/admin/filings/${f.id}/email`);
                            return data.email;
                          }}
                        />
                      </td>
                      <td className="px-5 py-3 text-slate-500 hidden sm:table-cell">
                        {f.created_at ? new Date(f.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-5 py-3 text-center">
                        {f.payment_status === "paid" ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Paid
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-50">
                            <Clock className="w-3 h-3 mr-1" /> Owes
                          </Badge>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right font-mono-data font-medium text-slate-900">{money(f.l14)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
